import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { interval } from 'rxjs';

import { GlobalPropertiesService } from './shared/modules/global-properties/services/global-properties.service';
import { LoopBackAuth } from './shared/sdk/services/core/auth.service';
import { IdleActivityService } from './shared/services/idle-activity.service';

import { ActiveToast, ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  static readonly WARNING_THRESHOLD = 120; // seconds (2 min) 
  public app;
  private warned = false;
  private warningToast: ActiveToast<any> | null = null;

  constructor(
    public globalProps: GlobalPropertiesService,
    protected auth: LoopBackAuth,
    private router: Router,
    private idleActivity: IdleActivityService,
    private toastr: ToastrService,
    private http: HttpClient // Injected HttpClient for token renewal
  ) {
    this.app = this.globalProps.appGlobal;
  }

  ngOnInit() {
    console.log('FS Frontend Loaded... Ready to rock. v' + this.globalProps.appGlobal.version);

    this.idleActivity.start();

    document.addEventListener('touchmove', function (event: any) {
      event = event.originalEvent || event;
      if (event.scale && event.scale !== 1) {
        event.preventDefault();
      }
    }, false);

    window.performance.mark('fs_loaded');

    const logoutCheckInterval = 30 * 1000;  // 30s

    interval(logoutCheckInterval).subscribe(x => {
      const token = this.auth.getToken();
      if (token && token.created) {
	// Safe date conversion regardless of whether created is string or Date object
        const createdTime = token.created instanceof Date ? token.created.getTime() : Date.parse(token.created);
        const expires = (createdTime + (token.ttl * 1000) - Date.now()) / 1000;
        console.log(`** Login expires in ${Math.floor(expires)} seconds`);

        if (expires > 0 && expires <= AppComponent.WARNING_THRESHOLD && !this.warned) {
          this.warned = true;

          this.warningToast = this.toastr.warning(
            'Click here to stay logged in.',
            'Session Expiring',
            {
              timeOut: 0,
              closeButton: true,
              tapToDismiss: false,
            }
          );

          // Call backend to issue a valid JWT when clicked
          this.warningToast.onTap.subscribe(() => {
            this.renewSession();
          });
        }

        // Reset warning state if activity or manual renewal pushed expires beyond threshold
        if (expires > AppComponent.WARNING_THRESHOLD) {
          this.warned = false;
          this.clearWarningToast();
        }

        // Handle session expiry
        if (expires <= 0) {
          console.log('Login has expired');

          this.clearWarningToast();
          this.auth.clear();
          this.router.navigate(['auth/login']);
        }
      }
    });
  }

  private renewSession(): void {
    const tokenObj = this.auth.getToken();
    const rawToken = tokenObj?.id;

    if (!rawToken || typeof rawToken !== 'string') {
      this.clearWarningToast();
      this.auth.clear();
      this.router.navigate(['auth/login']);
      return;
    }

    const url = `/api/1.0/FSUsers/refresh-token?access_token=${encodeURIComponent(rawToken)}`;
    const headers = { Authorization: `Bearer ${rawToken}` };

    this.http.post<any>(url, {}, { headers }).subscribe({
      next: (res) => {
        const currentToken = this.auth.getToken();
        if (currentToken) {
          currentToken.id = res.id;
          currentToken.ttl = res.ttl;
          currentToken.created = new Date();

          this.auth.setToken(currentToken);
          this.auth.save();
        }

        this.warned = false;
        this.clearWarningToast();
      },
      error: (err) => {
        console.error('Failed to renew session:', err);
        this.clearWarningToast();
        this.auth.clear();
        this.router.navigate(['auth/login']);
      }
    });
  }

  private clearWarningToast(): void {
    if (this.warningToast) {
      this.toastr.clear(this.warningToast.toastId);
      this.warningToast = null;
    }
  }
}

